import {
  aiFirstCreationStreamingState,
  getProcessedValueKey
} from '@databases/database-ai-first-creation-state';
import {
  type DatabaseStreamHookState,
  useDatabaseStreamState
} from '@databases/database-ai-first-creation-ui';
import { useRef } from 'react';
import { useSnapshot } from 'valtio';

import type { AiFieldCellRequestTarget } from './cell-request-coordinator/ai-field-cell-request-coordinator.ts';
import { useAiFieldCellRequestState } from './cell-request-coordinator/use-ai-field-cell-request-state.ts';
import { aiSummarySettlementState } from './summary/ai-summary-settlement-state.ts';
import { useIsAiSummaryFieldEnabled } from './summary/use-is-ai-summary-field-enabled.ts';
import { useIsAiFieldCellRunActive } from './use-is-ai-field-cell-streaming.ts';

export type AiFieldCellLoadingPhase = 'summarizing' | 'almost-done';
export type AiFieldCellLoadingState = AiFieldCellLoadingPhase | null;

export type UseAiFieldCellLoadingStateOptions = Omit<
  AiFieldCellRequestTarget,
  'isTargetValueEmpty'
> & {
  isTargetValueEmpty?: boolean;
};

type ActiveAiFieldStream = Extract<
  NonNullable<DatabaseStreamHookState['activeStream']>,
  { kind: 'ai-field' }
>;

export const useAiFieldCellLoadingState = ({
  contentId,
  aiFieldType,
  fieldId,
  entryId,
  isTargetValueEmpty = false
}: UseAiFieldCellLoadingStateOptions): AiFieldCellLoadingState => {
  const isAiSummaryFieldEnabled = useIsAiSummaryFieldEnabled();
  const requestState = useAiFieldCellRequestState({
    contentId,
    aiFieldType,
    fieldId,
    entryId,
    isTargetValueEmpty
  });
  const isCollaborativeRunActive = useIsAiFieldCellRunActive({
    contentId,
    aiFieldType,
    fieldId,
    entryId,
    isTargetValueEmpty
  });
  const { activeStream } = useDatabaseStreamState();
  const { activeAiFieldCell, processedValues } = useSnapshot(
    aiFirstCreationStreamingState
  );
  const { pendingValues, settledValues, settlingValues } = useSnapshot(
    aiSummarySettlementState
  );
  const almostDoneCellRef = useRef<{
    stream: ActiveAiFieldStream;
    fieldId: string;
    entryId: string;
  } | null>(null);

  if (!isAiSummaryFieldEnabled || aiFieldType !== 'summary') {
    almostDoneCellRef.current = null;
    return null;
  }

  const valueKey = getProcessedValueKey(fieldId, entryId);
  if (settledValues.has(valueKey)) {
    almostDoneCellRef.current = null;
    return null;
  }
  if (settlingValues.has(valueKey)) {
    return 'almost-done';
  }
  if (pendingValues.has(valueKey)) {
    return 'summarizing';
  }

  if (activeStream?.kind === 'ai-field') {
    const { entryIds, target } = activeStream;
    const isTargetCell =
      entryIds.has(entryId) &&
      target.aiFieldType === 'summary' &&
      target.fieldId === fieldId &&
      (target.scope === 'field' ||
        (target.scope === 'entry' && target.entryId === entryId) ||
        (target.scope === 'entries' && target.entryIds.includes(entryId)));

    if (isTargetCell) {
      if (processedValues.has(getProcessedValueKey(fieldId, entryId))) {
        almostDoneCellRef.current = null;
        return null;
      }

      const isActiveCell =
        activeAiFieldCell?.fieldId === fieldId && activeAiFieldCell.entryId === entryId;
      const hasReachedAlmostDone =
        almostDoneCellRef.current?.stream === activeStream &&
        almostDoneCellRef.current.fieldId === fieldId &&
        almostDoneCellRef.current.entryId === entryId;

      if (isActiveCell) {
        almostDoneCellRef.current = { stream: activeStream, fieldId, entryId };
      }

      if (isActiveCell || hasReachedAlmostDone) {
        return 'almost-done';
      }

      return 'summarizing';
    }
  }

  almostDoneCellRef.current = null;
  return requestState.status === 'idle' && !isCollaborativeRunActive
    ? null
    : 'summarizing';
};
